import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, X, CreditCard, ArrowDownCircle, ArrowUpCircle, Search, FileText, Paperclip, Upload, Eye, Undo2 } from 'lucide-react';
import { ActionGatedButton } from '../components/ActionGate';
import RefundPaymentModal from '../components/RefundPaymentModal';
import { useFocusEntity } from '../hooks/useFocusEntity';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertArabic } from 'arabic-reshaper';
import { NotoSansArabicBase64 } from '../fonts/NotoSansArabic';

import { useTranslation } from 'react-i18next';

// Helper function to process Arabic text for proper display in PDF
const processArabicText = (text) => {
  if (!text) return '';
  const arabicRegex = /[\u0600-\u06FF]/;
  if (!arabicRegex.test(text)) return text;
  return convertArabic(text);
};


/**
 * BankBackfillPanel · one-click tool that links legacy payments to
 * the bank ledger (bank_transactions).  Two-step safety:
 *   1. Preview (dry-run) — shows what would be created, no writes.
 *   2. Execute — actually inserts the bank_transactions rows.
 */
const BankBackfillPanel = () => {
  const [preview, setPreview] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [executing, setExecuting] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);

  const runPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await axiosInstance.post(
        '/payments/backfill-bank-transactions?dry_run=true');
      setPreview(data);
      if (data.created_count === 0) {
        toast.info('لا توجد دفعات تحتاج ربطاً بالبنك — كل شيء متصل بالفعل ✓');
      } else {
        toast.success(
          `سيتم ربط ${data.created_count} دفعة بالبنك (معاينة فقط، لم يُكتَب شيء بعد)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشلت المعاينة');
    } finally {
      setPreviewLoading(false);
    }
  };

  const runExecute = async () => {
    setExecuting(true);
    try {
      const { data } = await axiosInstance.post(
        '/payments/backfill-bank-transactions?dry_run=false');
      toast.success(`تم ربط ${data.created_count} دفعة بالبنك بنجاح ✓`);
      setPreview(data);
      setConfirming(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل التنفيذ');
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="mb-4 rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 to-white p-3"
         data-testid="bank-backfill-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="text-[13px] font-bold text-indigo-900">
            ربط الدفعات القديمة بالبنك
          </div>
          <div className="text-[11px] text-indigo-700">
            الدفعات المُنشأة قبل تحديث الشباط تحتاج ربطاً يدوياً بدفتر البنك.
            اضغط «معاينة» أولاً ثم «تنفيذ» إذا كانت النتيجة صحيحة.
          </div>
        </div>
        <button
          onClick={runPreview}
          disabled={previewLoading || executing}
          data-testid="backfill-preview-btn"
          className="text-[12px] rounded-md border border-indigo-300 bg-white
                     hover:bg-indigo-50 disabled:opacity-50
                     px-3 py-1.5 font-semibold text-indigo-800">
          {previewLoading ? 'جاري المعاينة...' : '👁 معاينة (Dry Run)'}
        </button>
        {preview && preview.created_count > 0 && !confirming && (
          <button
            onClick={() => setConfirming(true)}
            data-testid="backfill-confirm-open-btn"
            className="text-[12px] rounded-md border border-emerald-500 bg-emerald-500
                       hover:bg-emerald-600 text-white
                       px-3 py-1.5 font-semibold">
            ⚙ تنفيذ الربط الفعلي
          </button>
        )}
        {confirming && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-rose-800">
              متأكد؟ ستُنشأ {preview.created_count} حركة بنكية.
            </span>
            <button
              onClick={runExecute}
              disabled={executing}
              data-testid="backfill-execute-btn"
              className="text-[11px] rounded bg-rose-600 hover:bg-rose-700
                         disabled:opacity-50 text-white px-2 py-1 font-bold">
              {executing ? 'جاري التنفيذ...' : 'نعم، نفّذ'}
            </button>
            <button
              onClick={() => setConfirming(false)}
              disabled={executing}
              className="text-[11px] rounded border border-slate-300
                         bg-white text-slate-700 px-2 py-1">
              إلغاء
            </button>
          </div>
        )}
      </div>
      {preview && (
        <div className="mt-2 text-[11px]" data-testid="backfill-result">
          <div className="flex gap-3 font-mono text-slate-800">
            <span>Scanned: <b>{preview.scanned}</b></span>
            <span className="text-emerald-800">
              Created / would create: <b>{preview.created_count}</b>
            </span>
            <span className="text-slate-500">
              Skipped: <b>{preview.skipped_count}</b>
            </span>
            {preview.dry_run && (
              <span className="rounded bg-amber-100 text-amber-900 px-1.5 py-0.5">
                معاينة فقط
              </span>
            )}
          </div>
          {preview.created?.length > 0 && (
            <div className="mt-1 max-h-40 overflow-auto border border-slate-200 rounded">
              <table className="w-full text-[10px] font-mono">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left px-1.5 py-0.5">Payment #</th>
                    <th className="text-left px-1.5 py-0.5">Date</th>
                    <th className="text-left px-1.5 py-0.5">Type</th>
                    <th className="text-left px-1.5 py-0.5">Method</th>
                    <th className="text-right px-1.5 py-0.5">Amount</th>
                    <th className="text-left px-1.5 py-0.5">Bank</th>
                    <th className="text-left px-1.5 py-0.5">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.created.slice(0, 50).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100"
                        data-testid={`backfill-row-${i}`}>
                      <td className="px-1.5 py-0.5">{r.payment_no}</td>
                      <td className="px-1.5 py-0.5">{r.payment_date}</td>
                      <td className="px-1.5 py-0.5">{r.type}</td>
                      <td className="px-1.5 py-0.5">{r.method}</td>
                      <td className="px-1.5 py-0.5 text-right">
                        {Number(r.amount).toLocaleString()}
                      </td>
                      <td className="px-1.5 py-0.5">{r.bank_account_name}</td>
                      <td className="px-1.5 py-0.5">
                        <span className={r.action === 'created'
                          ? 'text-emerald-700 font-bold'
                          : 'text-amber-800'}>
                          {r.action}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};



const Payments = ({ user }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { focusedId, registerRow } = useFocusEntity();
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [supplierBills, setSupplierBills] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // Check if user can delete a payment
  const canDelete = (payment) => {
    if (user?.role === 'admin') return true;
    return payment.status === 'draft';
  };
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [refundingPayment, setRefundingPayment] = useState(null);

  const [formData, setFormData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_type: 'received',
    payment_method: 'cash',
    amount: '',
    reference_no: '',
    customer_id: '',
    supplier_id: '',
    invoice_id: '',
    supplier_bill_id: '',
    account_id: '',
    debit_account_id: '',  // حساب المدين للقيد
    credit_account_id: '', // حساب الدائن للقيد
    project_id: '',        // اسم المشروع (اختياري)
    description: '',
    notes: ''
  });

  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'check', label: 'Check' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchData();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  // Deep-link from Bank Transfer "Pay Supplier" CTA:
  //   /payments?type=made&account_id=<coa_id>
  // Opens the New Payment modal pre-filled so the user doesn't recreate
  // the context they just came from. Runs once per ?type= arrival.
  // (Accepts the legacy `paid` alias for back-compat with bookmarked URLs.)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawType = params.get('type');
    if (!rawType) return;
    const type = rawType === 'paid' ? 'made' : rawType;
    if (type !== 'made' && type !== 'received') return;
    setFormData((prev) => ({
      ...prev,
      payment_type: type,
      account_id: params.get('account_id') || prev.account_id,
    }));
    setEditingPayment(null);
    setShowModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchData = async () => {
    try {
      const [paymentsRes, customersRes, suppliersRes, invoicesRes, accountsRes, supplierBillsRes, projectsRes] = await Promise.all([
        axiosInstance.get('/payments'),
        axiosInstance.get('/customers'),
        axiosInstance.get('/suppliers'),
        axiosInstance.get('/invoices'),
        axiosInstance.get('/accounts'),
        axiosInstance.get('/supplier-bills'),
        axiosInstance.get('/projects'),
      ]);
      setPayments(paymentsRes.data);
      setCustomers(customersRes.data);
      setSuppliers(suppliersRes.data);
      setInvoices(invoicesRes.data);
      setAccounts(accountsRes.data.filter(a => a.is_bank_account || a.account_type === 'assets'));
      setSupplierBills(supplierBillsRes.data);
      setProjects(projectsRes.data || []);
    } catch (error) {
      if (loading) {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let paymentId;
      if (editingPayment) {
        await axiosInstance.put(`/payments/${editingPayment.id}`, formData);
        paymentId = editingPayment.id;
        toast.success('Payment updated successfully');
      } else {
        let response;
        try {
          response = await axiosInstance.post('/payments', formData);
        } catch (err) {
          // P6.3 — duplicate-payment auto-action
          const detail = err.response?.data?.detail;
          if (err.response?.status === 409 && detail?.code === 'PERIOD_LOCKED') {
            // P7+ Period lock
            toast.error(`🔒 ${detail.message}`, {
              description: `الفترة ${detail.period || ''} (${detail.period_status}) — Period locked`,
              duration: 6000,
            });
            return;
          }
          if (err.response?.status === 409 && detail?.code === 'DUPLICATE_PAYMENT_BLOCKED') {
            const msg = `⚠️ تنبيه — دفعة محتملة مكررة!\n\n` +
              `تم اكتشاف دفعة بنفس المبلغ (${detail.amount}) لـ ${detail.party_name || 'نفس الطرف'} ` +
              `قبل ${detail.hours_apart} ساعة (${detail.previous_payment_no}).\n\n` +
              `Possible duplicate payment detected.\n` +
              `Same amount (${detail.amount}) was paid to ${detail.party_name || 'the same party'} ` +
              `${detail.hours_apart}h ago (${detail.previous_payment_no}).\n\n` +
              `هل تريد المتابعة؟ ستتطلب الدفعة اعتماداً يدوياً. / Continue? Will require manual approval.`;
            if (!window.confirm(msg)) {
              toast.error(t('payments.payment_cancelled'));
              return;
            }
            response = await axiosInstance.post('/payments?confirm_duplicate=true', formData);
            toast.warning(t('payments.payment_created_with_duplicate_flag_awai'));
          } else {
            throw err;
          }
        }
        paymentId = response.data.id;
        if (response.data.approval_status === 'pending') {
          toast.success('Payment submitted for approval', {
            description: 'Waiting for admin approval before it appears in the list'
          });
        } else {
          toast.success('Payment created successfully');
        }
      }
      
      // Upload attachment if file is selected
      if (attachmentFile && paymentId) {
        setUploadingAttachment(true);
        const fileFormData = new FormData();
        fileFormData.append('file', attachmentFile);
        try {
          await axiosInstance.post(`/payments/${paymentId}/upload-attachment`, fileFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Attachment uploaded successfully');
        } catch (uploadError) {
          toast.error('Failed to upload attachment');
        }
        setUploadingAttachment(false);
      }
      
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payment');
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      payment_date: payment.payment_date,
      payment_type: payment.payment_type,
      payment_method: payment.payment_method,
      amount: payment.amount,
      reference_no: payment.reference_no || '',
      customer_id: payment.customer_id || '',
      supplier_id: payment.supplier_id || '',
      invoice_id: payment.invoice_id || '',
      // Critical Feb 2026 fix — seeding the bill linkage so editing a
      // payment that's ALREADY linked doesn't silently wipe the link
      // when the user re-saves without re-selecting the bill.
      supplier_bill_id: payment.supplier_bill_id || '',
      account_id: payment.account_id || '',
      debit_account_id: payment.debit_account_id || '',
      credit_account_id: payment.credit_account_id || '',
      project_id: payment.project_id || '',
      description: payment.description || '',
      notes: payment.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await axiosInstance.delete(`/payments/${id}`);
        toast.success('Payment deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete payment');
      }
    }
  };

  const resetForm = () => {
    setEditingPayment(null);
    setAttachmentFile(null);
    setFormData({
      payment_date: new Date().toISOString().split('T')[0],
      payment_type: 'received',
      payment_method: 'cash',
      amount: '',
      reference_no: '',
      customer_id: '',
      supplier_id: '',
      invoice_id: '',
      supplier_bill_id: '',
      account_id: '',
      debit_account_id: '',
      credit_account_id: '',
      project_id: '',
      description: '',
      notes: ''
    });
  };

  // Generate Payment Voucher PDF with Arabic support
  const generatePaymentPDF = async (payment) => {
    const doc = new jsPDF();
    const isReceived = payment.payment_type === 'received';
    
    // Add Arabic font to jsPDF
    doc.addFileToVFS('NotoSansArabic-Regular.ttf', NotoSansArabicBase64);
    doc.addFont('NotoSansArabic-Regular.ttf', 'NotoSansArabic', 'normal');

    // Company Info
    const companyName = 'Distinct High Road';
    const companyNameArabic = 'شركة الطريق العالي المتميز';
    const vatNumber = '314477769800003';
    
    // Header Background
    doc.setFillColor(10, 66, 117);
    doc.rect(0, 0, 210, 45, 'F');
    
    // Company Logo area
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(10, 8, 30, 30, 2, 2, 'F');
    
    // Company Name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(companyName, 50, 20);
    
    // Arabic company name using Arabic font
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(12);
    doc.text(processArabicText(companyNameArabic), 180, 15, { align: 'right' });
    
    // VAT Number
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`VAT: ${vatNumber}`, 50, 28);
    
    // Voucher Title
    doc.setFillColor(isReceived ? 34 : 220, isReceived ? 197 : 53, isReceived ? 94 : 69);
    doc.rect(0, 45, 210, 15, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const voucherTitle = isReceived ? 'RECEIPT VOUCHER' : 'PAYMENT VOUCHER';
    doc.text(voucherTitle, 70, 55);
    
    // Arabic title
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(14);
    doc.text(processArabicText(isReceived ? 'سند قبض' : 'سند صرف'), 180, 55, { align: 'right' });
    
    // Voucher Details
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    
    // Voucher Number and Date Box
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(10, 68, 90, 35, 3, 3, 'FD');
    doc.roundedRect(110, 68, 90, 35, 3, 3, 'FD');
    
    doc.setFont('helvetica', 'bold');
    doc.text('Voucher No:', 15, 80);
    doc.text('Date:', 15, 92);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(10, 66, 117);
    doc.text(payment.payment_no, 50, 80);
    doc.text(payment.payment_date, 50, 92);
    
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Reference:', 115, 80);
    doc.text('Method:', 115, 92);
    
    doc.setFont('helvetica', 'normal');
    doc.text(payment.reference_no || '-', 150, 80);
    doc.text(payment.payment_method.replace('_', ' ').toUpperCase(), 150, 92);
    
    // Party Details
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(10, 112, 190, 30, 3, 3, 'FD');
    
    doc.setFont('helvetica', 'bold');
    doc.text(isReceived ? 'Received From:' : 'Paid To:', 15, 125);
    doc.setFont('helvetica', 'normal');
    const partyName = isReceived ? (payment.customer_name || 'Cash Customer') : (payment.supplier_name || 'Cash Supplier');
    doc.text(partyName, 60, 125);
    
    if (payment.invoice_no) {
      doc.setFont('helvetica', 'bold');
      doc.text('Invoice:', 15, 135);
      doc.setFont('helvetica', 'normal');
      doc.text(payment.invoice_no, 60, 135);
    }
    
    // Amount Section
    doc.setFillColor(isReceived ? 220 : 254, isReceived ? 252 : 226, isReceived ? 231 : 226);
    doc.roundedRect(10, 150, 190, 40, 3, 3, 'F');
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Amount:', 15, 168);
    
    // Arabic Amount label
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(12);
    doc.text(processArabicText('المبلغ'), 45, 168);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.setTextColor(isReceived ? 34 : 220, isReceived ? 197 : 53, isReceived ? 94 : 69);
    doc.text(`${payment.amount.toLocaleString()} SAR`, 105, 175, { align: 'center' });
    
    // Description
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Description:', 15, 205);
    doc.setFont('helvetica', 'normal');
    const descText = payment.description || 'Payment';
    if (/[\u0600-\u06FF]/.test(descText)) {
      doc.setFont('NotoSansArabic', 'normal');
      doc.text(processArabicText(descText), 15, 215, { maxWidth: 180 });
    } else {
      doc.text(descText, 15, 215, { maxWidth: 180 });
    }
    
    // Notes
    if (payment.notes) {
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', 15, 230);
      doc.setFont('helvetica', 'normal');
      const notesText = payment.notes;
      if (/[\u0600-\u06FF]/.test(notesText)) {
        doc.setFont('NotoSansArabic', 'normal');
        doc.text(processArabicText(notesText), 15, 240, { maxWidth: 180 });
      } else {
        doc.text(notesText, 15, 240, { maxWidth: 180 });
      }
    }
    
    // Signature Section
    doc.setDrawColor(200, 200, 200);
    doc.line(15, 265, 80, 265);
    doc.line(130, 265, 195, 265);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Received By', 47.5, 273, { align: 'center' });
    doc.text('Approved By', 162.5, 273, { align: 'center' });
    
    // Arabic signature labels
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(9);
    doc.text(processArabicText('المستلم'), 47.5, 279, { align: 'center' });
    doc.text(processArabicText('المعتمد'), 162.5, 279, { align: 'center' });
    
    // Footer
    doc.setFillColor(10, 66, 117);
    doc.rect(0, 285, 210, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('This is a computer-generated document.', 105, 292, { align: 'center' });
    
    // Download PDF directly
    const fileName = isReceived ? `Receipt_${payment.payment_no}.pdf` : `Payment_${payment.payment_no}.pdf`;
    doc.save(fileName);
    toast.success('PDF downloaded');
  };

  // Filter payments
  const filteredPayments = payments.filter(payment => {
    const matchesType = filterType === 'all' || payment.payment_type === filterType;
    const matchesSearch = searchTerm === '' || 
      payment.payment_no.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (payment.customer_name && payment.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (payment.supplier_name && payment.supplier_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (payment.reference_no && payment.reference_no.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesType && matchesSearch;
  });

  // Calculate totals
  const totalReceived = payments.filter(p => p.payment_type === 'received').reduce((sum, p) => sum + p.amount, 0);
  const totalMade = payments.filter(p => p.payment_type === 'made').reduce((sum, p) => sum + p.amount, 0);

  // Map every received payment → its refund-history aggregate.
  // Pure derivation from the already-fetched list; the backend writes
  // `parent_payment_id` and negative `amount` on every refund row.
  const refundsByParent = useMemo(() => {
    const m = {};
    for (const p of payments) {
      if (p.payment_type === 'refund' && p.parent_payment_id) {
        const slot = (m[p.parent_payment_id] ||= { count: 0, total: 0, items: [] });
        slot.count += 1;
        slot.total += Math.abs(p.amount || 0);
        slot.items.push(p);
      }
    }
    return m;
  }, [payments]);

  if (loading) {
    return <div className="text-center py-12">Loading payments...</div>;
  }

  return (
    <div data-testid="payments-page">
      <div className="page-header">
        <h2 className="page-title">Payments</h2>
        <button
          data-testid="add-payment-button"
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New Payment
        </button>
      </div>

      <BankBackfillPanel />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="stat-card">
          <div className="stat-icon green">
            <ArrowDownCircle className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Received</h3>
          <p className="text-3xl font-bold text-green-600">{totalReceived.toLocaleString()} SAR</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon red">
            <ArrowUpCircle className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Paid Out</h3>
          <p className="text-3xl font-bold text-red-600">{totalMade.toLocaleString()} SAR</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue">
            <CreditCard className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Net Balance</h3>
          <p className={`text-3xl font-bold ${totalReceived - totalMade >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {(totalReceived - totalMade).toLocaleString()} SAR
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search payments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input w-64"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterType('received')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'received' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Received
            </button>
            <button
              onClick={() => setFilterType('made')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'made' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Paid Out
            </button>
          </div>
        </div>
      </div>

      {/* Payments Table - Split View */}
      <div className="flex gap-4">
        <div className={`table-container transition-all duration-300 ${selectedPayment ? 'w-1/2' : 'w-full'}`}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Payment No</th>
                <th>Date</th>
                <th>Type</th>
                <th>Customer/Supplier</th>
                <th>Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.length > 0 ? (
                filteredPayments.map((payment) => (
                  <React.Fragment key={payment.id}>
                  <tr 
                    ref={registerRow(payment.id)}
                    data-testid={`payment-row-${payment.id}`}
                    onClick={() => setSelectedPayment(selectedPayment?.id === payment.id ? null : payment)}
                    className={`cursor-pointer hover:bg-blue-50 transition-colors ${selectedPayment?.id === payment.id ? 'bg-blue-100 border-l-4 border-blue-500' : ''} ${focusedId === payment.id ? 'focus-highlight' : ''}`}
                  >
                    <td className="font-semibold text-blue-600">{payment.payment_no}</td>
                    <td>{new Date(payment.payment_date).toLocaleDateString()}</td>
                    <td>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        payment.payment_type === 'received' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {payment.payment_type === 'received' ? (
                          <><ArrowDownCircle className="w-3 h-3" /> Received</>
                        ) : (
                          <><ArrowUpCircle className="w-3 h-3" /> Paid Out</>
                        )}
                      </span>
                    </td>
                    <td>
                      {payment.payment_type === 'received' 
                        ? payment.customer_name || '-'
                        : payment.supplier_name || '-'
                      }
                    </td>
                    <td className={`font-bold ${payment.payment_type === 'received' ? 'text-green-600' : 'text-red-600'}`}>
                      <div className="flex items-center gap-2">
                        <span>{payment.payment_type === 'received' ? '+' : '-'}{payment.amount.toLocaleString()} SAR</span>
                        {payment.attachment_url && (
                          <Paperclip className="w-4 h-4 text-blue-500" title="Has attachment" />
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                        {payment.attachment_url && (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${payment.attachment_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-icon text-purple-600 hover:bg-purple-50"
                            title="View Attachment"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          onClick={() => generatePaymentPDF(payment)}
                          className="btn-icon text-blue-600 hover:bg-blue-50"
                          title="Download PDF"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(payment)}
                          className="btn-icon edit"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {payment.payment_type === 'received' && (
                          <ActionGatedButton
                            action="payment.refund"
                            ctx={payment}
                            onClick={() => setRefundingPayment(payment)}
                            data-testid={`refund-payment-${payment.id}`}
                            className="btn-icon text-rose-600 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            title="استرداد"
                          >
                            <Undo2 className="w-4 h-4" />
                          </ActionGatedButton>
                        )}
                        {canDelete(payment) && (
                          <button
                            onClick={() => handleDelete(payment.id)}
                            className="btn-icon delete"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {payment.payment_type === 'received' && refundsByParent[payment.id] && (
                    <tr
                      data-testid={`refund-history-${payment.id}`}
                      className="bg-rose-50/40 text-xs"
                    >
                      <td colSpan="6" className="px-4 py-1.5 text-rose-700 border-b border-rose-100">
                        <span className="font-medium">↩ استُرد منها</span>{' '}
                        <span className="font-mono">{refundsByParent[payment.id].count}</span> دفعة بإجمالي{' '}
                        <span className="font-mono font-semibold">
                          {refundsByParent[payment.id].total.toLocaleString('ar-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SAR
                        </span>
                        {' '}— متبقٍ{' '}
                        <span className="font-mono font-semibold text-emerald-700">
                          {Math.max(0, (payment.amount || 0) - refundsByParent[payment.id].total).toLocaleString('ar-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SAR
                        </span>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-gray-500">
                    No payments found. Create your first payment!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Payment Details Panel */}
        {selectedPayment && (
          <div className="w-1/2 bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedPayment.payment_no}</h3>
                <p className="text-sm text-gray-500">
                  {selectedPayment.payment_type === 'received' ? 'Receipt Voucher' : 'Payment Voucher'}
                </p>
              </div>
              <button 
                onClick={() => setSelectedPayment(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Type Badge & Actions */}
            <div className="flex items-center gap-3 mb-6">
              <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                selectedPayment.payment_type === 'received' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {selectedPayment.payment_type === 'received' ? (
                  <><ArrowDownCircle className="w-4 h-4" /> Payment Received</>
                ) : (
                  <><ArrowUpCircle className="w-4 h-4" /> Payment Made</>
                )}
              </span>
              <button
                onClick={() => generatePaymentPDF(selectedPayment)}
                className="btn-primary text-sm py-1 px-3"
              >
                <FileText className="w-4 h-4 inline mr-1" />
                PDF
              </button>
            </div>

            {/* Amount */}
            <div className={`rounded-lg p-4 mb-4 ${selectedPayment.payment_type === 'received' ? 'bg-green-50' : 'bg-red-50'}`}>
              <p className="text-sm text-gray-600 mb-1">Amount</p>
              <p className={`text-3xl font-bold ${selectedPayment.payment_type === 'received' ? 'text-green-600' : 'text-red-600'}`}>
                {selectedPayment.payment_type === 'received' ? '+' : '-'}{selectedPayment.amount.toLocaleString()} SAR
              </p>
            </div>

            {/* Payment Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Payment Details</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Date:</span>
                  <p className="font-medium">{new Date(selectedPayment.payment_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Method:</span>
                  <p className="font-medium capitalize">{selectedPayment.payment_method?.replace('_', ' ')}</p>
                </div>
                <div>
                  <span className="text-gray-500">Reference:</span>
                  <p className="font-medium">{selectedPayment.reference_no || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Account:</span>
                  <p className="font-medium">{selectedPayment.account_name || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Party Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">
                {selectedPayment.payment_type === 'received' ? 'Customer' : 'Supplier'}
              </h4>
              <p className="font-medium">
                {selectedPayment.payment_type === 'received' 
                  ? selectedPayment.customer_name || 'Cash Customer'
                  : selectedPayment.supplier_name || 'Cash Supplier'
                }
              </p>
              {selectedPayment.invoice_no && (
                <p className="text-sm text-purple-600 mt-1">Invoice: {selectedPayment.invoice_no}</p>
              )}
            </div>

            {/* Description */}
            {selectedPayment.description && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-700 mb-1">Description</h4>
                <p className="text-sm text-gray-600">{selectedPayment.description}</p>
              </div>
            )}

            {/* Notes */}
            {selectedPayment.notes && (
              <div className="bg-yellow-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-700 mb-1">Notes</h4>
                <p className="text-sm text-gray-600">{selectedPayment.notes}</p>
              </div>
            )}

            {/* Attachment */}
            {selectedPayment.attachment_url && (
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachment
                </h4>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${selectedPayment.attachment_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  data-testid="view-payment-attachment"
                >
                  <Eye className="w-4 h-4" />
                  View Attachment
                </a>
              </div>
            )}

            {/* Created Info */}
            <div className="text-xs text-gray-400">
              Created by: {selectedPayment.user_name || 'N/A'}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingPayment ? 'Edit Payment' : 'New Payment'}
              </h3>
              <button onClick={() => setShowModal(false)} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Payment Date */}
                <div className="form-group">
                  <label className="form-label">Payment Date *</label>
                  <input
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>

                {/* Payment Type */}
                <div className="form-group">
                  <label className="form-label">Payment Type *</label>
                  <select
                    value={formData.payment_type}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      payment_type: e.target.value,
                      customer_id: '',
                      supplier_id: ''
                    })}
                    className="form-select"
                    required
                  >
                    <option value="received">Payment Received (From Customer)</option>
                    <option value="made">Payment Made (To Supplier)</option>
                  </select>
                </div>

                {/* Payment Method */}
                <div className="form-group">
                  <label className="form-label">Payment Method *</label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    className="form-select"
                    required
                  >
                    {paymentMethods.map(method => (
                      <option key={method.value} value={method.value}>{method.label}</option>
                    ))}
                  </select>
                </div>

                {/* Amount */}
                <div className="form-group">
                  <label className="form-label">Amount (SAR) *</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || '' })}
                    className="form-input"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                {/* Customer (for received) */}
                {formData.payment_type === 'received' && (
                  <div className="form-group">
                    <label className="form-label">Customer</label>
                    <select
                      value={formData.customer_id}
                      onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                      className="form-select"
                    >
                      <option value="">Select Customer</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.id}>{customer.name_english || customer.name_arabic}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Supplier (for made) */}
                {formData.payment_type === 'made' && (
                  <div className="form-group">
                    <label className="form-label">Supplier</label>
                    <select
                      value={formData.supplier_id}
                      onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value, supplier_bill_id: '' })}
                      className="form-select"
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>{supplier.name_english || supplier.name_arabic}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Invoice (for received payments) */}
                {formData.payment_type === 'received' && (
                  <div className="form-group">
                    <label className="form-label">Related Invoice</label>
                    <select
                      value={formData.invoice_id}
                      onChange={(e) => setFormData({ ...formData, invoice_id: e.target.value })}
                      className="form-select"
                    >
                      <option value="">Select Invoice (Optional)</option>
                      {invoices.map(invoice => (
                        <option key={invoice.id} value={invoice.id}>
                          {invoice.invoice_no} - {invoice.customer_name} ({invoice.total.toLocaleString()} SAR)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Supplier Bill (for made payments) */}
                {formData.payment_type === 'made' && (
                  <div className="form-group">
                    <label className="form-label">Related Supplier Bill</label>
                    <select
                      value={formData.supplier_bill_id}
                      onChange={(e) => setFormData({ ...formData, supplier_bill_id: e.target.value })}
                      className="form-select"
                    >
                      <option value="">Select Supplier Bill (Optional)</option>
                      {supplierBills
                        .filter(bill => !formData.supplier_id || bill.supplier_id === formData.supplier_id)
                        .map(bill => (
                          <option key={bill.id} value={bill.id}>
                            {bill.bill_number} - {bill.supplier_name} ({(bill.total || 0).toLocaleString()} SAR)
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {/* Account */}
                <div className="form-group">
                  <label className="form-label">Bank/Cash Account</label>
                  <select
                    value={formData.account_id}
                    onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                    className="form-select"
                  >
                    <option value="">Select Account</option>
                    {accounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.account_name} {account.is_bank_account ? '(Bank)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Reference No */}
                <div className="form-group">
                  <label className="form-label">Reference No</label>
                  <input
                    type="text"
                    value={formData.reference_no}
                    onChange={(e) => setFormData({ ...formData, reference_no: e.target.value })}
                    className="form-input"
                    placeholder="Check no, Transfer ref, etc."
                  />
                </div>

                {/* Project (optional) — sourced from /api/projects */}
                <div className="form-group">
                  <label className="form-label" data-testid="payment-project-label">
                    {t('payments.project_name_optional')}
                  </label>
                  <select
                    value={formData.project_id}
                    onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                    className="form-select"
                    data-testid="payment-project-select"
                  >
                    <option value="">{t('payments.no_project')}</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name || p.project_name || p.name_arabic || p.name_english || p.id}
                        {p.code ? ` (${p.code})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Journal Entry Accounts Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Journal Entry Accounts (حسابات القيد اليومي)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Debit Account */}
                  <div className="form-group mb-0">
                    <label className="form-label text-blue-700">
                      {formData.payment_type === 'received' ? 'Debit Account (مدين) - Cash/Bank' : 'Debit Account (مدين) - Expense'}
                    </label>
                    <select
                      value={formData.debit_account_id}
                      onChange={(e) => setFormData({ ...formData, debit_account_id: e.target.value })}
                      className="form-select"
                    >
                      <option value="">Auto Select (تلقائي)</option>
                      {accounts.filter(a => 
                        formData.payment_type === 'received' 
                          ? (a.is_bank_account || a.account_type === 'assets')
                          : (a.account_type === 'expenses' || a.account_type === 'liabilities')
                      ).map(account => (
                        <option key={account.id} value={account.id}>
                          {account.account_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Credit Account */}
                  <div className="form-group mb-0">
                    <label className="form-label text-blue-700">
                      {formData.payment_type === 'received' ? 'Credit Account (دائن) - Revenue' : 'Credit Account (دائن) - Cash/Bank'}
                    </label>
                    <select
                      value={formData.credit_account_id}
                      onChange={(e) => setFormData({ ...formData, credit_account_id: e.target.value })}
                      className="form-select"
                    >
                      <option value="">Auto Select (تلقائي)</option>
                      {accounts.filter(a => 
                        formData.payment_type === 'received' 
                          ? (a.account_type === 'revenue' || a.account_type === 'liabilities')
                          : (a.is_bank_account || a.account_type === 'assets')
                      ).map(account => (
                        <option key={account.id} value={account.id}>
                          {account.account_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  * Leave empty for automatic account selection based on payment type
                </p>
              </div>

              {/* Description */}
              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-input"
                  placeholder="Payment description"
                />
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="form-input"
                  rows="2"
                  placeholder="Additional notes"
                />
              </div>

              {/* Attachment Upload */}
              <div className="form-group">
                <label className="form-label flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachment (PDF/Image)
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                      attachmentFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                    }`}>
                      {attachmentFile ? (
                        <div className="flex items-center justify-center gap-2 text-green-600">
                          <FileText className="w-5 h-5" />
                          <span className="font-medium">{attachmentFile.name}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setAttachmentFile(null);
                            }}
                            className="ml-2 text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1 text-gray-500">
                          <Upload className="w-6 h-6" />
                          <span className="text-sm">Click to upload file</span>
                          <span className="text-xs text-gray-400">PDF, PNG, JPG (Max 10MB)</span>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 10 * 1024 * 1024) {
                            toast.error('File size must be less than 10MB');
                            return;
                          }
                          setAttachmentFile(file);
                        }
                      }}
                      className="hidden"
                      data-testid="payment-attachment-input"
                    />
                  </label>
                </div>
                {editingPayment?.attachment_url && !attachmentFile && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-blue-600">
                    <FileText className="w-4 h-4" />
                    <span>Current attachment:</span>
                    <a 
                      href={`${process.env.REACT_APP_BACKEND_URL}${editingPayment.attachment_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-blue-800"
                    >
                      View File
                    </a>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button type="submit" className="btn-primary flex-1" disabled={uploadingAttachment}>
                  {uploadingAttachment ? 'Uploading...' : (editingPayment ? 'Update Payment' : 'Create Payment')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {refundingPayment && (
        <RefundPaymentModal
          payment={refundingPayment}
          onClose={() => setRefundingPayment(null)}
          onRefunded={() => fetchData()}
        />
      )}
    </div>
  );
};

export default Payments;
